/**
 * World Broadcast Service
 *
 * Extracted broadcast helpers for world object CRUD and edit locking.
 * Thin wrappers around Supabase Realtime channel.send().
 */

import { getChannel, getPresenceKey } from './realtimeManager';
import { useWorldStore } from '../stores/worldStore';
import { persistObjectAdd, persistObjectUpdate, persistObjectDelete } from './worldPersistence';
import { toOriginalUrls } from '../stores/liteStore';

// ---- Object CRUD broadcasts ----
// Each sender also writes through to world_assets (per-object persistence),
// so builders' changes survive without the owner's manual save. Receivers
// never persist — only the originating client does.

export function broadcastObjectAdd(worldId: string, objectType: string, id: string, item: any) {
  try {
    getChannel(worldId).send({
      type: 'broadcast',
      event: 'world:object:added',
      payload: { objectType, id, item: toOriginalUrls(item) }, // peers get originals, never this device's lite files
    });
  } catch (_) { /* channel not ready */ }
  persistObjectAdd(worldId, objectType, id, item);
}

export function broadcastObjectUpdate(worldId: string, objectType: string, id: string, updates: any) {
  try {
    getChannel(worldId).send({
      type: 'broadcast',
      event: 'world:object:updated',
      payload: { objectType, id, updates: toOriginalUrls(updates) },
    });
  } catch (_) { /* channel not ready */ }
  persistObjectUpdate(worldId, objectType, id);
}

export function broadcastObjectDelete(worldId: string, objectType: string, id: string) {
  try {
    getChannel(worldId).send({
      type: 'broadcast',
      event: 'world:object:deleted',
      payload: { objectType, id },
    });
  } catch (_) { /* channel not ready */ }
  persistObjectDelete(worldId, objectType, id);
}

// ---- Edit lock broadcasts ----

export function broadcastObjectLock(worldId: string, objectId: string) {
  const key = getPresenceKey();
  const userName = useWorldStore.getState().userName;
  try {
    getChannel(worldId).send({
      type: 'broadcast',
      event: 'world:object:lock',
      payload: { id: objectId, userId: key, userName },
    });
  } catch (_) { /* channel not ready */ }
}

export function broadcastObjectUnlock(worldId: string, objectId: string) {
  try {
    getChannel(worldId).send({
      type: 'broadcast',
      event: 'world:object:unlock',
      payload: { id: objectId },
    });
  } catch (_) { /* channel not ready */ }
}

// ---- Emote broadcast ----
// Tells other players to play this emote on the sender's remote avatar.
// nonce (Date.now()) lets the same emote be re-triggered. Ephemeral — never
// persisted.

export function broadcastEmote(worldId: string, name: string | null) {
  const key = getPresenceKey();
  try {
    getChannel(worldId).send({
      type: 'broadcast',
      event: 'player:emote',
      payload: { key, name, nonce: Date.now() },
    });
  } catch (_) { /* channel not ready */ }
}

/** "I'm presenting my screen" — others connect to the voice room to watch. */
export function broadcastScreenLive(worldId: string, live: boolean) {
  try {
    getChannel(worldId).send({ type: 'broadcast', event: 'screen:live', payload: { key: getPresenceKey(), live } });
  } catch (_) { /* channel not ready */ }
}

// ---- Live transform broadcast (throttled by caller) ----

export function broadcastObjectTransform(
  worldId: string,
  objectId: string,
  transform: { position?: [number, number, number]; rotation?: [number, number, number]; scale?: [number, number, number] }
) {
  try {
    getChannel(worldId).send({
      type: 'broadcast',
      event: 'world:object:transform',
      payload: { id: objectId, ...transform },
    });
  } catch (_) { /* channel not ready */ }
}
